import { formatUpdatedAt } from '../lib/format';
import type { HowToStep } from '../lib/types';

interface HeaderProps {
  title?: string;
  tagline?: string;
  howTo?: HowToStep[];
}

/**
 * LifeGroups branding leads; the Crossroads mark is secondary and lives in the
 * footer. The wordmark is black on transparent, so it sits on white only.
 */
export function Header({ title, tagline, howTo }: HeaderProps) {
  return (
    <header className="site-header">
      <div className="shell">
        {/* width/height match the SVG's tight viewBox so the box is reserved
            before the asset loads and nothing shifts. */}
        <img
          className="site-header__logo"
          src="/brand/lifegroups-logo.svg"
          alt="Crossroads LifeGroups"
          width={742}
          height={355}
        />
        <h1 className="site-header__title">{title || 'Find Your LifeGroup'}</h1>
        {tagline && <p className="site-header__tagline">{tagline}</p>}
        <HowTo steps={howTo} />
      </div>
    </header>
  );
}

/**
 * Three numbered steps between the tagline and the filters.
 *
 * Most people arriving here have been sent a link in a text and have never seen
 * the page — the filter bar reads as decoration until someone says it is a
 * filter. Kept to one clause per step: it is orientation, not instructions, and
 * anything longer becomes a wall to scroll past on a phone. The copy lives in
 * config.json so Groups staff can reword it without a deploy.
 */
export function HowTo({ steps }: { steps?: HowToStep[] }) {
  if (!steps?.length) return null;

  return (
    <ol className="howto">
      {steps.map((step, index) => (
        <li className="howto__step" key={step.label}>
          <span className="howto__num" aria-hidden="true">
            {index + 1}
          </span>
          <span className="howto__copy">
            <b className="howto__label">{step.label}</b> {step.text}
          </span>
        </li>
      ))}
    </ol>
  );
}

export function Footer({ note, updatedAt }: { note?: string; updatedAt?: string | null }) {
  const updated = formatUpdatedAt(updatedAt);

  return (
    <footer className="site-footer">
      <div className="shell">
        {note && <p className="site-footer__note">{note}</p>}
        <img
          className="site-footer__logo"
          src="/brand/crossroads-lockup.svg"
          alt="Crossroads Church"
          width={3299}
          height={487}
          loading="lazy"
        />

        {/*
          Deliberately the quietest thing on the page: last line, smallest type,
          faintest color. There for anyone checking how current the list is —
          invisible to everyone else.
        */}
        {updated && (
          <p className="site-footer__updated">
            <time dateTime={updatedAt ?? undefined}>{updated}</time>
          </p>
        )}
      </div>
    </footer>
  );
}

export function EmptyState({ onClear, query }: { onClear: () => void; query?: string }) {
  const searched = query?.trim();

  return (
    <div className="empty">
      {/* Quoting the term back is the difference between "nothing matched" and
          "nothing matched *that*" — the second tells someone their typo is the
          problem. */}
      <p className="empty__headline">
        {searched ? <>No groups match “{searched}”.</> : 'No groups match those filters.'}
      </p>
      <p className="empty__body">
        {searched
          ? 'Try a shorter word, a leader’s name, or a day — or clear everything to see every group this term.'
          : 'Try widening your search — or clear the filters to see every group this term.'}
      </p>
      <button type="button" className="btn btn--primary" onClick={onClear}>
        Clear filters
      </button>
    </div>
  );
}

export function LoadingState() {
  return (
    <div className="empty" role="status">
      <p className="empty__body">Loading groups…</p>
    </div>
  );
}

/**
 * Shown for the few seconds after a deploy, while the new container is pulling
 * a fresh list from CCB. The alternative was serving the committed sample —
 * last term's groups — which looked authoritative and was wrong.
 */
export function RefreshingState() {
  return (
    <div className="empty" role="status">
      <p className="empty__headline">Updating the group list…</p>
      <p className="empty__body">
        We're pulling the latest groups from our church database. This only takes a moment.
      </p>
    </div>
  );
}

export function ErrorState({ message }: { message: string }) {
  return (
    <div className="empty" role="alert">
      <p className="empty__headline">We couldn't load the group list.</p>
      <p className="empty__body">{message}</p>
      <button type="button" className="btn btn--primary" onClick={() => window.location.reload()}>
        Try again
      </button>
    </div>
  );
}
